import os from "os";
import chalk from "chalk";
import ora from "ora";
import { runCommand } from "./exec.js";
import { promptQuestion } from "./prompt.js";

export interface PortProcess {
  pid: number;
  name: string;
}

/**
 * Scan a port to see if a process is listening on it.
 */
export async function findProcessOnPort(port: number): Promise<PortProcess | null> {
  const platform = os.platform();

  if (platform === "win32") {
    try {
      const netstatResult = await runCommand("netstat -ano");
      if (netstatResult.error) return null;

      const lines = netstatResult.stdout.split("\n");
      for (const line of lines) {
        if (line.includes(`:${port}`) && line.includes("LISTENING")) {
          const parts = line.trim().split(/\s+/);
          const pid = parseInt(parts[parts.length - 1], 10);
          if (!isNaN(pid)) {
            let name = "Unknown";
            const taskResult = await runCommand(`tasklist /FI "PID eq ${pid}" /NH`);
            if (!taskResult.error && taskResult.stdout.trim().length > 0) {
              const taskParts = taskResult.stdout.trim().split(/\s+/);
              if (taskParts[0]) name = taskParts[0];
            }
            return { pid, name };
          }
        }
      }
    } catch {
      return null;
    }
  } else {
    // macOS / Linux
    try {
      const lsofResult = await runCommand(`lsof -i :${port} -sTCP:LISTEN`);
      if (lsofResult.error || lsofResult.stdout.trim().length === 0) return null;

      const lines = lsofResult.stdout.split("\n").map(l => l.trim()).filter(l => l.length > 0);
      if (lines.length > 1) {
        const parts = lines[1].split(/\s+/);
        const name = parts[0];
        const pid = parseInt(parts[1], 10);
        if (!isNaN(pid)) {
          return { pid, name };
        }
      }
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Terminate a process by PID.
 */
export async function killProcess(pid: number): Promise<boolean> {
  const platform = os.platform();
  const cmd = platform === "win32" ? `taskkill /F /PID ${pid}` : `kill -9 ${pid}`;
  const result = await runCommand(cmd);
  return !result.error;
}

/**
 * Checks for port conflicts and offers interactive termination.
 */
export async function checkAndResolvePortConflict(port: number): Promise<boolean> {
  const processInfo = await findProcessOnPort(port);
  if (!processInfo) return true; // No conflict

  console.log(chalk.yellow(`\n⚠️  Port ${port} is currently occupied by PID ${processInfo.pid} (${processInfo.name}).`));
  
  if (process.stdout.isTTY) {
    const answer = await promptQuestion(`Would you like to terminate this process and free port ${port}? (y/n): `);
    if (answer.toLowerCase() === "y" || answer.toLowerCase() === "yes") {
      const killSpinner = ora(`Terminating process ${processInfo.pid}...`).start();
      const killed = await killProcess(processInfo.pid);
      
      if (killed) {
        killSpinner.succeed(`Terminated process ${processInfo.pid}. Port ${port} is now free!`);
        // Brief sleep to let OS fully release port
        await new Promise(resolve => setTimeout(resolve, 1000));
        return true;
      } else {
        killSpinner.fail(`Failed to terminate process ${processInfo.pid}.`);
        return false;
      }
    }
  } else {
    console.log(chalk.red(`✖ Cannot launch Metro: Port ${port} is occupied by PID ${processInfo.pid}.`));
  }

  return false;
}
